import { tokyoYear, toWareki } from "../datetime.ts";
import type { SensitivityRules } from "../review-due.ts";
import { COMMON_RULES } from "./common.ts";
import type { ProfessionPack } from "./types.ts";

const yearLabel = (y: number) => `${y}年（${toWareki(y)}）`;

/** What in a registration-and-succession article changes with the law: fees and amounts, requirements, time limits, dates, reforms. */
const SCRIVENER_SENSITIVITY: SensitivityRules = {
  amount: { label: "金額・登録免許税", pattern: /\d[\d,，.]*\s?(?:万円|円|%|％)|登録免許税|過料|報酬/ },
  relief: { label: "制度・要件", pattern: /要件|義務化|義務|過料|対象(?:と|に)なる|制度|特例|免税/ },
  deadline: { label: "期限・時効", pattern: /期限|時効|(?:\d+|[一二三四五六七八九十]+)(?:か月|ヶ月|カ月|年)以内|月\d+日まで/ },
  year: { label: "時点の書き方", pattern: /令和\d+年|\d{4}年(?:時点|現在|\d+月)|施行日/ },
  reform: { label: "改正・施行の記述", pattern: /改正|施行|義務化|予定|見込み|新設|廃止|創設/ },
};

/**
 * 司法書士. The expression rules follow the 司法書士法 and the
 * federation's advertising rules: no promised outcomes, no comparison with
 * other 司法書士, and no litigation beyond the 簡易裁判所 limit.
 */
export const JUDICIAL_SCRIVENER: ProfessionPack = {
  id: "judicial_scrivener",
  label: "司法書士",
  supervisorTitle: "司法書士",
  registrationLabel: "司法書士登録番号",
  reviewer: "司法書士の先生",
  writingRules: [
    "依頼主の司法書士事務所の立場で書く。文体は「です・ます」。一人称と表記は事務所の設定に従う。",
    "断定してよいのは、法令、法務省・裁判所などの公的な一次資料に根拠がある場合だけ。根拠とした法令名・資料名を出典として示す。",
    "法令は現行のものを書く。改正は施行日を明記し、施行前のものは「予定」と書く。",
    "登録免許税・手数料・期限などの数字は、一次資料で確認できたものだけを書く。報酬は事務所の設定にあるものだけを書く。",
    "結果の保証（「必ず通る」「確実に減額」など）、根拠のない最上級、他の司法書士事務所との比較は書かない。",
    "司法書士が扱える範囲（登記、供託、簡易裁判所の代理は認定司法書士で140万円以下、成年後見など）を超える業務を扱えるように書かない。超える内容は弁護士との連携として書く。",
    "個別の相談に当たる断定的な助言はしない。個別の判断が必要な内容は「ご相談ください」とし、CTA につなげる。",
    "他のサイトの文章を写さない。競合の情報は構成の参考と、足りない点の把握にだけ使う。",
    "検索した人の知りたいことに冒頭で答える。必要に応じて FAQ を入れる。",
    "文字数稼ぎの水増しをしない。",
  ],
  expressionRules: [
    ...COMMON_RULES,
    { id: "scrivener-outcome", pattern: /(必ず|確実に|絶対に?)(通|認められ|減額|免責|解決|取り戻せ)/g, severity: "high", category: "結果の保証", message: "手続きや交渉の結果を保証する表現は使えません", suggestion: "「〜が認められる場合があります」とし、要件と流れを説明する" },
    { id: "scrivener-comparison", pattern: /他の(司法書士|事務所)(より|と比べ|とは違|には(でき|ない))|どこよりも/g, severity: "high", category: "他の司法書士との比較", message: "他の司法書士事務所との比較は、広告として認められていません", suggestion: "比較をやめ、当事務所の取り扱い方を事実として説明する" },
    { id: "scrivener-scope", pattern: /(地方裁判所|高等裁判所|140万円を超え).{0,12}(代理|対応でき)|離婚(調停|裁判)の代理|刑事(事件|弁護)/g, severity: "high", category: "扱えない業務", message: "司法書士が扱えない業務を扱えるように読める表現です", suggestion: "「弁護士と連携して対応します」など、扱える範囲に書き直す" },
    { id: "scrivener-specialist", pattern: /(相続|登記|債務整理)専門(の)?(司法書士|事務所)|スペシャリスト/g, severity: "low", category: "「専門」の表示", message: "「専門」の表示は、客観的な根拠を示せる場合に限るのが安全です", suggestion: "「〜を重点的に取り扱っています」と書く" },
    { id: "scrivener-future", pattern: /改正(され|になり)ます|(施行|適用)されます/g, severity: "low", category: "改正の書き方", message: "施行前の内容なら、施行日と「予定」を明記する必要があります", suggestion: "「〜年〜月〜日から施行される予定です（〇年〇月時点）」と書く" },
  ],
  trustedDomains: ["moj.go.jp", "houmukyoku.moj.go.jp", "courts.go.jp", "laws.e-gov.go.jp", "elaws.e-gov.go.jp", "nta.go.jp", "houterasu.or.jp", "shiho-shoshi.or.jp", "touki.or.jp", "mlit.go.jp", "fsa.go.jp"],
  authorityDomains: ["bengo4.com", "souzoku.asahi.com", "vs-group.jp", "chester-tax.com", "nikkei.com", "ja.wikipedia.org", "suumo.jp"],
  specialtyOptions: ["相続登記・遺産承継", "遺言・家族信託", "成年後見", "不動産登記（売買・贈与・抵当権）", "会社設立・商業登記", "債務整理（任意整理・過払い）", "空き家・所有者不明土地", "裁判所への書類作成", "外国人の登記・在留", "信託登記"],
  seasons: [
    { id: "nenmatsu-nenshi", name: "年末年始の帰省（相続の話し合い）", start: { month: 12, day: 25 }, end: { month: 1, day: 5 }, leadWeeks: 6, terms: ["相続登記", "相続", "遺言", "名義変更", "遺産分割"], note: "家族が集まり、親の家の名義や遺言の話題が出やすい時期です。" },
    { id: "nendo-matsu", name: "年度末の売買・会社の手続き", start: { month: 2, day: 1 }, end: { month: 3, day: 31 }, leadWeeks: 8, terms: ["所有権移転", "抵当権 抹消", "会社設立", "役員変更", "決算"], note: "引っ越しと不動産の決済、新年度に向けた会社の手続きが集中します。" },
    { id: "shinnendo", name: "新年度の会社の登記", start: { month: 4, day: 1 }, end: { month: 6, day: 30 }, leadWeeks: 6, terms: ["役員変更登記", "本店移転", "定款変更", "株主総会"], note: "株主総会のあとの役員変更の登記が6月に集中します。" },
    { id: "obon", name: "お盆の帰省（相続・空き家の話し合い）", start: { month: 8, day: 1 }, end: { month: 8, day: 20 }, leadWeeks: 6, terms: ["相続登記 義務化", "空き家", "実家", "成年後見", "家族信託"], note: "実家の今後や親の老後を家族で話す時期です。相続登記の義務化で検索が増えています。" },
    { id: "keiro", name: "敬老の日の前後（遺言・後見）", start: { month: 9, day: 10 }, end: { month: 9, day: 30 }, leadWeeks: 6, terms: ["遺言書", "成年後見", "任意後見", "家族信託", "終活"], note: "親の老後や遺言について、家族で考えるきっかけになる時期です。" },
    { id: "nenmatsu-shiharai", name: "年末の支払い（借金の整理）", start: { month: 11, day: 15 }, end: { month: 12, day: 31 }, leadWeeks: 6, terms: ["債務整理", "任意整理", "過払い金", "借金"], note: "年末は支払いが重なり、借金の整理を考える人が調べる時期です。" },
  ],
  officeNoun: "司法書士事務所",
  officeExample: "山田司法書士事務所",
  affiliationExample: "大阪司法書士会",
  sourcesPhrase: "法務省や裁判所などの公的な資料",
  sourceExamples: "法務省・法務局・裁判所・e-Gov 法令検索",
  keyFigures: "登録免許税・期限・金額",
  publishers: { "houmukyoku.moj.go.jp": "法務局", "moj.go.jp": "法務省", "courts.go.jp": "裁判所", "e-gov.go.jp": "e-Gov 法令検索", "nta.go.jp": "国税庁", "houterasu.or.jp": "法テラス（日本司法支援センター）", "shiho-shoshi.or.jp": "日本司法書士会連合会", "touki.or.jp": "民事法務協会（登記情報提供サービス）", "mlit.go.jp": "国土交通省", "fsa.go.jp": "金融庁" },
  period: {
    label: yearLabel,
    short: (y) => `${y}年`,
    rules: (y) => `${yearLabel(y)}時点の法令`,
    brief: (y) => `基準とする時点：${yearLabel(y)}時点の法令（現行の法令で書く。改正は施行日を明記し、施行前のものは「予定」と書く）`,
    mustState: false,
    current: tokyoYear,
  },
  review: { name: "法改正の見直し", when: "法改正の施行（4月1日が多い）や新しい年のあと", nowLabel: "現在", currentArticle: "最新の法令に合った記事", updateLabel: "法改正の反映", sensitivity: SCRIVENER_SENSITIVITY },
  features: { articles: true },
};
